import { type SpecCitationSegment, splitCitationMarkers } from '@parcel/types';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import * as React from 'react';
import { cn } from '../lib/utils.js';
import { Popover, PopoverAnchor, PopoverContent } from './popover.js';
import { Skeleton } from './skeleton.js';

/** One cited record as a pill shows it; the host app fills the label from its own lookup. */
export interface ParcelCitation {
  /** The record's identity; one record cited twice in a group shows once. */
  key: string;
  /** Null while it loads. */
  label: string | null;
  kind: string;
  detail?: string | null;
  /** The member cannot see it (deleted or out of reach): it is left out, never shown. */
  unavailable?: boolean;
  selected?: boolean;
  onOpen?: () => void;
  /** The host's mark for the source (favicon, photo, initials), drawn in source lists. */
  icon?: React.ReactNode;
  /** A short name for where it lives (a web page's domain); the pill shows it over the label. */
  site?: string | null;
}

/** A message's sources: saved `[n]` markers and live `[S3]` handles. */
export interface ParcelCitations {
  cite(n: number): ParcelCitation | null;
  live(handle: string): ParcelCitation | null;
  /** Draw nothing for a saved marker with no source (a live answer's stray `[1]`). */
  hideUnresolved?: boolean;
}

const CitationsContext = React.createContext<ParcelCitations | null>(null);

export function ParcelCitationsProvider({
  value,
  children,
}: {
  value: ParcelCitations;
  children?: React.ReactNode;
}) {
  return <CitationsContext.Provider value={value}>{children}</CitationsContext.Provider>;
}

/** The element the remark plugin emits; mapped to a pill in the markdown components. */
export const CITATION_TAG = 'parcel-cite';

interface MdNode {
  type: string;
  value?: string;
  children?: MdNode[];
  data?: Record<string, unknown>;
}

type Marker = Exclude<SpecCitationSegment, { type: 'text' }>;

// Their text is not prose: a link label, code, raw HTML, a footnote label.
const SKIP = new Set(['link', 'linkReference', 'inlineCode', 'code', 'definition', 'html']);
const TRAILING_PUNCTUATION = /^[.,;:!?]+/;

const markerText = (marker: Marker) =>
  marker.type === 'cite' ? `[${marker.n}]` : `[${marker.handle}]`;

/** Markers side by side (or apart by spaces) form one group; the space before and the
 * punctuation after travel with it, so a resolved pill can sit after the sentence. */
function citationNodes(text: string): MdNode[] {
  const segments = splitCitationMarkers(text);
  const out: MdNode[] = [];
  let index = 0;
  while (index < segments.length) {
    const segment = segments[index] as SpecCitationSegment;
    index += 1;
    if (segment.type === 'text') {
      out.push({ type: 'text', value: segment.text });
      continue;
    }
    const group: Marker[] = [segment];
    while (index < segments.length) {
      const next = segments[index] as SpecCitationSegment;
      const after = segments[index + 1];
      if (next.type === segment.type) {
        group.push(next as Marker);
        index += 1;
      } else if (next.type === 'text' && /^\s+$/.test(next.text) && after?.type === segment.type) {
        index += 1;
      } else break;
    }
    const previous = out.at(-1);
    const lead = previous?.value?.match(/\s+$/)?.[0] ?? '';
    if (previous?.value && lead) previous.value = previous.value.slice(0, -lead.length);
    const next = segments[index];
    const trail = next?.type === 'text' ? (next.text.match(TRAILING_PUNCTUATION)?.[0] ?? '') : '';
    if (next?.type === 'text' && trail) {
      segments[index] = { ...next, text: next.text.slice(trail.length) };
    }
    out.push({
      type: 'parcelCitation',
      data: {
        hName: CITATION_TAG,
        hProperties: {
          [segment.type === 'cite' ? 'dataCites' : 'dataLive']: group
            .map((marker) => (marker.type === 'cite' ? String(marker.n) : marker.handle))
            .join(','),
          dataTrail: trail,
        },
        hChildren: [{ type: 'text', value: `${lead}${group.map(markerText).join('')}${trail}` }],
      },
    });
  }
  return out.filter((node) => node.type !== 'text' || node.value !== '');
}

function walk(node: MdNode): void {
  if (!node.children) return;
  node.children = node.children.flatMap((child) => {
    if (child.type === 'text' && typeof child.value === 'string') {
      return child.value.includes('[') ? citationNodes(child.value) : [child];
    }
    if (!SKIP.has(child.type)) walk(child);
    return [child];
  });
}

function groupsIn(node: MdNode, out: { parent: MdNode; node: MdNode }[] = []) {
  for (const child of node.children ?? []) {
    if (child.type === 'parcelCitation') out.push({ parent: node, node: child });
    else groupsIn(child, out);
  }
  return out;
}

function groupKey(node: MdNode): string {
  const props = (node.data?.hProperties ?? {}) as Record<string, string>;
  return [...new Set((props.dataCites ?? props.dataLive ?? '').split(','))].sort().join(',');
}

/** A source cited again and again is cited once, at the end of the run; a live answer holds the
 * run it is still writing until the next source starts one. */
function collapseRuns(tree: MdNode, live: boolean): void {
  const groups = groupsIn(tree);
  groups.forEach((group, index) => {
    const next = groups[index + 1];
    if (next ? groupKey(next.node) !== groupKey(group.node) : !live) return;
    const trail = ((group.node.data?.hProperties ?? {}) as Record<string, string>).dataTrail ?? '';
    const at = group.parent.children?.indexOf(group.node) ?? -1;
    group.parent.children?.splice(at, 1, ...(trail ? [{ type: 'text', value: trail }] : []));
  });
}

/** Turns `[n]` and `[S3]` in prose into citation elements; links and code are left alone. */
export function remarkCitations(options: { live?: boolean } = {}) {
  return (tree: MdNode) => {
    walk(tree);
    collapseRuns(tree, options.live === true);
  };
}

const PARTIAL_HANDLE = /\[S(?:[1-9]\d{0,3}(?:[ \t]*,[ \t]*(?:S(?:[1-9]\d{0,3})?)?)*)?$/;

/** Live text with a handle still being typed cut off, so `[S1` never shows. */
export function trimPartialCitation(text: string): string {
  return text.replace(PARTIAL_HANDLE, '');
}

function nameOf(citation: ParcelCitation): string {
  return citation.label ?? 'Loading';
}

/** A list of sources, one clickable row each: mark, name, and kind with detail. */
export function ParcelCitationList({
  citations,
  onPicked,
}: {
  citations: readonly ParcelCitation[];
  /** Called after a row opens its source, so the host can close its popover. */
  onPicked?: () => void;
}) {
  return (
    <ul aria-label="Sources" className="flex flex-col">
      {citations.map((citation) => (
        <li key={citation.key}>
          <button
            type="button"
            disabled={!citation.onOpen}
            data-selected={citation.selected ? '' : undefined}
            onClick={() => {
              citation.onOpen?.();
              onPicked?.();
            }}
            className="flex w-full items-center gap-2.5 rounded-sm px-2 py-1.5 text-left transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-default disabled:opacity-60 data-[selected]:bg-muted"
          >
            {citation.icon ? <span className="flex shrink-0">{citation.icon}</span> : null}
            <span className="flex min-w-0 flex-col">
              {citation.label ? (
                <span className="truncate text-foreground text-sm">{citation.label}</span>
              ) : (
                <Skeleton className="my-1 h-3 w-32" />
              )}
              <span className="truncate text-muted-foreground text-xs">
                {[citation.kind, citation.detail].filter(Boolean).join(' · ')}
              </span>
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}

const PILL_CLASS =
  'inline-flex max-w-44 items-center gap-1 rounded-md bg-muted px-1.5 font-normal text-[11px] text-muted-foreground leading-[18px] transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring aria-disabled:cursor-default aria-disabled:opacity-60 aria-disabled:hover:bg-muted aria-disabled:hover:text-muted-foreground';

const OPEN_DELAY_MS = 120;
const CLOSE_DELAY_MS = 150;

/** A source's mark scaled to a small round chip. */
function SmallMark({ citation, className }: { citation: ParcelCitation; className?: string }) {
  if (!citation.icon) return null;
  return (
    <span
      className={cn(
        'flex size-4 shrink-0 items-center justify-center overflow-hidden rounded-full [&>*]:!size-4',
        className,
      )}
    >
      {citation.icon}
    </span>
  );
}

/** One source in the card: where it lives, its name as a link, and a line of detail. */
function SourceCardBody({
  citation,
  onOpened,
}: {
  citation: ParcelCitation;
  onOpened: () => void;
}) {
  const where = citation.site ?? citation.kind;
  // A web page's detail is its domain, already shown above the title.
  const detail = citation.detail && citation.detail !== citation.site ? citation.detail : null;
  return (
    <div className="space-y-1 px-3 py-2.5" data-slot="citation-card-source">
      <div className="flex min-w-0 items-center gap-2 text-muted-foreground text-xs">
        <SmallMark citation={citation} />
        <span className="truncate">{where}</span>
      </div>
      <button
        type="button"
        disabled={!citation.onOpen}
        onClick={() => {
          citation.onOpen?.();
          onOpened();
        }}
        title={citation.label ?? undefined}
        className="line-clamp-2 block w-full text-left font-medium text-foreground text-sm underline-offset-2 transition-colors hover:text-brand hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-default disabled:hover:text-foreground disabled:hover:no-underline"
      >
        {citation.label ?? <Skeleton className="my-1 h-3 w-40" />}
      </button>
      {detail ? <p className="line-clamp-2 text-muted-foreground text-xs">{detail}</p> : null}
    </div>
  );
}

/** aria-disabled, not disabled: a disabled arrow drops keyboard focus, and the card closes on the blur. */
const PAGER_ARROW =
  'rounded p-1 transition-colors hover:bg-muted hover:text-foreground aria-disabled:cursor-default aria-disabled:opacity-40 aria-disabled:hover:bg-transparent aria-disabled:hover:text-muted-foreground';

/** A pill and its hover card: one source, or several to page through. Same for both. */
function CitationPill({ citations }: { citations: readonly ParcelCitation[] }) {
  const first = citations[0] as ParcelCitation;
  const more = citations.length - 1;
  const openable = citations.find((citation) => citation.onOpen);
  const selected = citations.some((citation) => citation.selected);
  const [open, setOpen] = React.useState(false);
  const [page, setPage] = React.useState(0);
  const timer = React.useRef<number | undefined>(undefined);
  // The pointer over the card or pill holds it open; a blur alone (a pager arrow
  // disabling under the click) must not close it.
  const pointerIn = React.useRef(false);
  const cardRef = React.useRef<HTMLDivElement>(null);
  const current = citations[Math.min(page, citations.length - 1)] as ParcelCitation;
  const label = `Source: ${nameOf(first)}${more > 0 ? ` and ${more} more` : ''}`;

  React.useEffect(() => () => window.clearTimeout(timer.current), []);
  // Hover and focus open it directly, never through onOpenChange, so a reopen starts at the first source here.
  React.useEffect(() => {
    if (!open) setPage(0);
  }, [open]);
  const schedule = (next: boolean, delay: number) => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setOpen(next), delay);
  };
  const show = () => schedule(true, OPEN_DELAY_MS);
  const hide = () => schedule(false, CLOSE_DELAY_MS);
  const keep = () => window.clearTimeout(timer.current);
  const enter = (then: () => void) => () => {
    pointerIn.current = true;
    then();
  };
  const leave = () => {
    pointerIn.current = false;
    hide();
  };
  const blurred = (event: React.FocusEvent) => {
    const next = event.relatedTarget as Node | null;
    if (pointerIn.current || (next && cardRef.current?.contains(next))) return;
    hide();
  };
  const close = () => {
    keep();
    setOpen(false);
  };

  return (
    <span data-slot="assistant-citation" data-selected={selected ? '' : undefined}>
      <Popover
        open={open}
        onOpenChange={(next) => {
          if (next) setPage(0);
          setOpen(next);
        }}
      >
        <PopoverAnchor asChild>
          <button
            type="button"
            aria-label={label}
            aria-disabled={openable ? undefined : true}
            aria-expanded={open}
            onMouseEnter={enter(show)}
            onMouseLeave={leave}
            onFocus={() => {
              keep();
              setOpen(true);
            }}
            onBlur={blurred}
            // One source opens on click; several keep the card open to choose from.
            onClick={() => (more > 0 ? setOpen(true) : openable?.onOpen?.())}
            className={cn(PILL_CLASS, selected && 'bg-accent text-foreground ring-1 ring-border')}
          >
            {first.label ? (
              <span className="truncate">{first.site ?? first.label}</span>
            ) : (
              <span
                data-slot="citation-loading"
                aria-hidden
                className="h-2 w-12 animate-pulse rounded-sm bg-foreground/10"
              />
            )}
            {more > 0 ? <span className="shrink-0 opacity-70">+{more}</span> : null}
          </button>
        </PopoverAnchor>
        <PopoverContent
          side="bottom"
          align="start"
          sideOffset={6}
          className="w-80 overflow-hidden rounded-xl p-0"
          data-slot="citation-card"
          ref={cardRef}
          onMouseEnter={enter(keep)}
          onMouseLeave={leave}
          onFocusCapture={keep}
          onBlurCapture={blurred}
          onOpenAutoFocus={(event) => event.preventDefault()}
        >
          {more > 0 ? (
            <div className="flex items-center justify-between gap-2 border-b px-2 py-1.5">
              <div className="flex items-center gap-0.5 text-muted-foreground text-xs">
                <button
                  type="button"
                  aria-label="Previous source"
                  aria-disabled={page === 0 ? true : undefined}
                  onClick={() => setPage((value) => Math.max(0, value - 1))}
                  className={PAGER_ARROW}
                >
                  <ChevronLeft className="size-3.5" aria-hidden />
                </button>
                <span className="min-w-8 text-center tabular-nums" data-slot="citation-card-page">
                  {page + 1}/{citations.length}
                </span>
                <button
                  type="button"
                  aria-label="Next source"
                  aria-disabled={page >= citations.length - 1 ? true : undefined}
                  onClick={() => setPage((value) => Math.min(citations.length - 1, value + 1))}
                  className={PAGER_ARROW}
                >
                  <ChevronRight className="size-3.5" aria-hidden />
                </button>
              </div>
              <div className="flex items-center gap-1.5 pr-1 text-muted-foreground text-xs">
                <span className="flex -space-x-1">
                  {citations.slice(0, 3).map((citation) => (
                    <SmallMark
                      key={citation.key}
                      citation={citation}
                      className="ring-2 ring-popover"
                    />
                  ))}
                </span>
                {citations.length} sources
              </div>
            </div>
          ) : null}
          <SourceCardBody citation={current} onOpened={close} />
        </PopoverContent>
      </Popover>
    </span>
  );
}

/** Renders one `CITATION_TAG` element: a pill after its punctuation, or the marker's own text. */
export function ParcelCitationElement(props: {
  'data-cites'?: string;
  'data-live'?: string;
  'data-trail'?: string;
  children?: React.ReactNode;
}) {
  const citations = React.useContext(CitationsContext);
  const live = props['data-live'];
  const trail = props['data-trail'] ?? '';
  const found = !citations
    ? []
    : live !== undefined
      ? live.split(',').map((handle) => citations.live(handle))
      : (props['data-cites'] ?? '').split(',').map((n) => citations.cite(Number(n)));
  const unique = found.filter(
    (citation, index): citation is ParcelCitation =>
      citation !== null &&
      !citation.unavailable &&
      found.findIndex((other) => other?.key === citation.key) === index,
  );
  if (unique.length > 0) {
    return (
      <>
        {trail} <CitationPill citations={unique} />
      </>
    );
  }
  // A handle or a record the member cannot see shows nothing; a marker with no source keeps its text.
  const hidden = live !== undefined || citations?.hideUnresolved || found.some(Boolean);
  return hidden ? <>{trail}</> : <>{props.children}</>;
}
